import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connectionApi,
  type ConnectionOperation,
} from '../../lib/connections-api';
import { useAuth } from '../../lib/auth';
import { Icon } from '../ui/Icon';
import { abilityName } from './ConnectionAbilities';
import activityStyles from '../chat/Chat.module.css';
import styles from './Connections.module.css';
export function ConnectionOperationCard({
  operationId,
  compact = false,
}: {
  operationId: string;
  compact?: boolean;
}) {
  const { bootstrap } = useAuth();
  const scope = `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
  const cache = useQueryClient();
  const query = useQuery({
    queryKey: ['connection-operation', scope, operationId],
    queryFn: () => connectionApi.operation(operationId),
    refetchInterval: (q) =>
      ['succeeded', 'failed', 'cancelled'].includes(q.state.data?.status ?? '')
        ? false
        : 5000,
  });
  const connection = useQuery({
    queryKey: ['connection', scope, query.data?.connection_id],
    queryFn: () => connectionApi.detail(query.data!.connection_id),
    enabled: !!query.data?.connection_id,
  });
  const [response, setResponse] = useState('');
  const [continuationId, setContinuationId] = useState<string | null>(null);
  const command = useMutation({
    mutationFn: ({ action, op }: { action: string; op: ConnectionOperation }) =>
      connectionApi.command(op.id, action, {
        expectedRevision: op.revision,
        ...(['approve', 'resume'].includes(action) ? { requestHash: op.request_hash } : {}),
        ...(action === 'respond' ? { message: response } : {}),
      }),
    onSuccess: (data, variables) => {
      if (variables.action === 'reauthorize') {
        setContinuationId(data.id);
        void cache.invalidateQueries({ queryKey: ['connection-operations'] });
        return;
      }
      setResponse('');
      cache.setQueryData(['connection-operation', scope, operationId], data);
      void cache.invalidateQueries({ queryKey: ['connection-operations'] });
    },
  });
  if (query.isLoading) return compact
    ? <div className={activityStyles.tool} data-status="running" role="status">
        <span className={activityStyles.toolIcon}><Icon name="tools" size={13} /></span>
        Use connection…
      </div>
    : <p>Loading connection operation…</p>;
  if (!query.data)
    return (
      <p role="alert">
        This connection result is unavailable.{' '}
        <button onClick={() => void query.refetch()}>Retry</button>
      </p>
    );
  const op = query.data,
    cap = connection.data?.capabilities.find((c) => c.id === op.capability_id),
    endpoint = connection.data?.endpoints.find(
      (e) => e.id === cap?.endpoint_id,
    );
  const canResume = op.status === 'unknown' && endpoint?.metadata.replaySafeTools?.some((contract) => contract.tool === cap?.remote_id);
  const run = (action: string) => command.mutate({ action, op });
  if (compact && op.status === 'succeeded') {
    const label = `Used ${connection.data?.name ?? 'connection'}${cap ? ` · ${abilityName(cap.title)}` : ''}`;
    return <a className={`${activityStyles.tool} ${activityStyles.connectionTool}`} data-status="succeeded"
      href={`/app/connections/${op.connection_id}`} title={label}>
      <span className={activityStyles.toolIcon}><Icon name="circle-check" size={13} /></span>
      <span className={activityStyles.toolSummary}>{label}</span>
    </a>;
  }
  return (
    <article className={styles.card} aria-label="Connection operation">
      <div className={styles.row}>
        <strong>
          {op.result ? 'Used' : op.dispatched_at ? 'Requested' : 'Review'}{' '}
          {connection.data?.name ?? 'connection'} ·{' '}
          {cap?.title ?? 'External operation'}
        </strong>
        <span>{op.status.replaceAll('_', ' ')}</span>
      </div>
      {endpoint?.metadata.identity != null && (
        <div className={styles.muted}>
          Remote identity (as reported):{' '}
          {JSON.stringify(endpoint.metadata.identity)}
        </div>
      )}
      {endpoint && (
        <p className={styles.muted}>
          {endpoint.url} · Confirm the account and environment from provider
          evidence before approving consequential work.
        </p>
      )}
      {cap?.description && <details><summary>About this capability</summary><p>{cap.description}</p></details>}
      <dl className={styles.fields}>
        {Object.entries(op.request).map(([key, value]) => (
          <div key={key} style={{ display: 'contents' }}>
            <dt>{key}</dt>
            <dd>
              {typeof value === 'string'
                ? value
                : JSON.stringify(value, null, 2)}
            </dd>
          </div>
        ))}
      </dl>
      <details>
        <summary>Structured inputs</summary>
        <pre className={styles.result}>
          {JSON.stringify(op.request, null, 2)}
        </pre>
      </details>
      {op.status === 'awaiting_approval' && (
        <>
          <p className={styles.muted}>
            Review the exact action and data shared with this system. Delegated
            agents choose their own internal steps. External service charges may
            apply.
          </p>
          <button
            disabled={command.isPending || !connection.data}
            onClick={() => run('approve')}
          >
            Approve this request
          </button>
        </>
      )}
      {op.status === 'input_required' && (
        <label>
          Reply to this task
          <textarea
            value={response}
            maxLength={16000}
            onChange={(e) => setResponse(e.target.value)}
          />
          <button
            disabled={!response.trim() || command.isPending}
            onClick={() => run('respond')}
          >
            Send this input to the agent
          </button>
        </label>
      )}
      {continuationId && (
        <ConnectionOperationCard operationId={continuationId} compact={compact} />
      )}
      {op.status === 'auth_required' && (
        <button disabled={command.isPending} onClick={() => run('reauthorize')}>
          After reconnecting, review resuming this task
        </button>
      )}
      {op.status === 'auth_required' && (
        <a href={`/app/connections/${op.connection_id}`}>
          Reconnect this endpoint to continue. Never put credentials in a task
          reply.
        </a>
      )}
      {op.inputs?.map((input) => (
        <div key={input.messageId}>
          <strong>Shared input</strong>
          <p>{input.text}</p>
          <span className={styles.muted}>{input.sharedAt}</span>
        </div>
      ))}
      {op.result && (
        <>
          <p className={styles.muted}>
            {op.result.evidence === 'agent_reported'
              ? 'Agent-reported result · delivery/payment not independently verified'
              : 'Provider response · inspect returned evidence'}{' '}
            · {String(op.result.retrievedAt ?? '')}
          </p>
          <pre className={styles.result}>
            {JSON.stringify(op.result.content, null, 2)}
          </pre>
        </>
      )}
      {op.safe_error && <p role="status">{op.safe_error}</p>}
      {op.remote_state === 'rejected' && (
        <p>The remote agent rejected this task.</p>
      )}
      {op.status === 'unknown' && (
        <p>
          The external request may have completed. Check the remote system
          before preparing another request. The reviewed inputs remain saved. Captures are not saved until a receipt confirms them; automatic write replay is disabled.
        </p>
      )}
      {canResume && <button disabled={command.isPending} onClick={() => run('resume')}>Check receipt and resume original approved request</button>}
      {!['succeeded', 'failed', 'cancelled'].includes(op.status) && (
        <div className={styles.row}>
          <button disabled={command.isPending} onClick={() => run('refresh')}>
            Check progress
          </button>
          <button
            disabled={command.isPending || op.status === 'cancel_requested'}
            onClick={() => run('cancel')}
          >
            {op.dispatched_at ? 'Request cancellation' : 'Cancel request'}
          </button>
        </div>
      )}
      {op.conversation_id && (
        <a href={`/app/c/${op.conversation_id}`}>Open originating chat</a>
      )}
      {command.isError && (
        <p className={styles.error} role="alert">
          {command.error.message}
        </p>
      )}
    </article>
  );
}
